import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";
import { api } from "@/lib/brands";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll get back to you.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  const field =
    "w-full border-2 border-hair bg-transparent px-4 py-3 font-body text-ink transition-colors focus:border-accent focus:outline-none";

  return (
    <div className="pb-16">
      <Seo
        title="Contact It's Genie Assist — Request a Brand or Report a Number"
        description="Contact It's Genie Assist to request a brand listing, report an outdated customer service number, or ask a question. We typically reply within 1–2 business days."
      />
      <section className="border-b border-hair py-12 sm:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">Contact</p>
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
          Get in touch.
        </h1>
        <p className="mt-4 max-w-2xl text-muted">
          Spotted an outdated number? Want a brand added? Represent a company and need a correction?
          Send a note. We read everything.
        </p>
      </section>

      <div className="grid gap-px bg-hair md:grid-cols-[1.3fr_1fr]">
        <form onSubmit={onSubmit} className="bg-paper p-6 sm:p-8" data-testid="contact-form">
          <div className="grid gap-5">
            <div>
              <label htmlFor="c-name" className="font-mono text-[11px] uppercase tracking-widest text-muted">
                Your name
              </label>
              <input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className={`mt-2 ${field}`} />
            </div>
            <div>
              <label htmlFor="c-email" className="font-mono text-[11px] uppercase tracking-widest text-muted">
                Email
              </label>
              <input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={set("email")} className={`mt-2 ${field}`} />
            </div>
            <div>
              <label htmlFor="c-subject" className="font-mono text-[11px] uppercase tracking-widest text-muted">
                Subject
              </label>
              <input id="c-subject" data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className={`mt-2 ${field}`} />
            </div>
            <div>
              <label htmlFor="c-message" className="font-mono text-[11px] uppercase tracking-widest text-muted">
                Message
              </label>
              <textarea id="c-message" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={`mt-2 ${field}`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="inline-flex items-center justify-center bg-accent px-6 py-3.5 font-mono text-sm uppercase tracking-widest text-paper transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>

        <aside className="bg-paper p-6 sm:p-8">
          <h2 className="font-serif text-xl font-medium text-ink">Before you write</h2>
          <ul className="mt-4 space-y-4 text-sm text-muted">
            <li>
              <span className="font-mono text-accent">·</span> We can&apos;t access your account with
              any brand — we only publish their public contact info.
            </li>
            <li>
              <span className="font-mono text-accent">·</span> We never ask for passwords, payment,
              or one-time codes. Neither should anyone claiming to be us.
            </li>
            <li>
              <span className="font-mono text-accent">·</span> To report a wrong number, include the
              brand name and where you found the correct one.
            </li>
          </ul>
        </aside>
      </div>

      <section className="mt-12 grid gap-10 border-t border-hair pt-10 md:grid-cols-2" data-testid="contact-info">
        <div>
          <h2 className="font-serif text-2xl font-medium text-ink">What we can help with</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted">
            <li className="flex gap-2"><span className="text-accent">·</span> Requesting a new brand or category to be added to the directory.</li>
            <li className="flex gap-2"><span className="text-accent">·</span> Reporting an outdated, incorrect or changed customer service number or link.</li>
            <li className="flex gap-2"><span className="text-accent">·</span> Brand or trademark owners requesting a correction to their listing.</li>
            <li className="flex gap-2"><span className="text-accent">·</span> General questions about how the independent directory works.</li>
          </ul>
        </div>
        <div>
          <h2 className="font-serif text-2xl font-medium text-ink">Response time</h2>
          <p className="mt-4 text-sm leading-relaxed text-muted">
            Most messages are answered within <span className="text-ink">1–2 business days</span>. For an
            urgent, time-sensitive problem, you&apos;ll get help fastest through the brand&apos;s official
            contact listed on its page, or via our optional{" "}
            <Link to="/get-help" className="text-accent underline">specialist help</Link>.
          </p>
          <h2 className="mt-8 font-serif text-2xl font-medium text-ink">Common questions</h2>
          <dl className="mt-4 space-y-4 text-sm leading-relaxed text-muted">
            <div>
              <dt className="font-medium text-ink">Are you affiliated with the brands you list?</dt>
              <dd className="mt-1">No. We are an independent directory and publish only public, official contact information — sourced from each brand&apos;s own website with the date we verified it.</dd>
            </div>
            <div>
              <dt className="font-medium text-ink">Can you access or fix my account?</dt>
              <dd className="mt-1">We can&apos;t access your account with any brand. We point you to the correct official support so you can resolve it directly, and we never ask for passwords, payment or one-time codes.</dd>
            </div>
            <div>
              <dt className="font-medium text-ink">How do I report a wrong number?</dt>
              <dd className="mt-1">Use the form and include the brand name plus where you found the correct number — we&apos;ll verify and update it.</dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
}
